import isEmpty from 'lodash-es/isEmpty';
import React, { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Button } from '../../../components/Button';
import { Flex } from '../../../components/Flex';
import { Input } from '../../../components/Input';
import { Text } from '../../../components/Text';
import { ForgotPasswordNewPasswordDto } from '../../../model/user/dto/ForgotPasswordNewPasswordDto';
import { useAppDispatch } from '../../../redux/store';
import { userActions } from '../../../redux/user/user.slice';
import { FontSize } from '../../../theme/FontSize';
import { Rounded } from '../../../theme/Rounded';
import { Spacing } from '../../../theme/Spacing';
import { AtiraToast } from '../../../utils/AtiraToast';
import { onSetTypedCRUDErrors } from '../../../utils/onSetTypedCRUDErrors';
import { ForgotPasswordBackStepButton } from './ForgotPasswordBackStepButton';

const Wrapper = styled(Flex)`
  justify-content: center;
  align-items: center;
  flex-direction: column;
  width: 100%;
  gap: ${Spacing.s};
  margin-top: ${Spacing.xl};
  padding-inline: ${Spacing.s};
`;

const StyledButton = styled(Button)`
  height: 3.5rem;
  width: 100%;
  background-color: ${({ theme }) => theme.main};
  transition: background-color 0.2s;
  margin: 0;
  margin-top: ${Spacing.m};
  border-radius: ${Rounded.lg} !important;

  &:hover {
    background-color: ${({ theme }) => theme.mainHover};
  }
`;

const StyledInput = styled(Input)`
  height: 3.5rem;
  border: 2px solid ${({ theme }) => theme.fgSecondary}30 !important;
  padding: 0.5rem;
  transition: border 0.2s;
  font-size: ${FontSize.m};
  border-radius: ${Rounded.lg};
  background-color: ${({ theme }) => theme.transparent};

  &:focus {
    border: 2px solid #86b7fe !important;
  }
`;

type ForgotPasswordNewPasswordProps = {
  nextStep: VoidFunction;
  prevStep: VoidFunction;
};

export const ForgotPasswordNewPassword: React.FC<
  ForgotPasswordNewPasswordProps
> = ({ nextStep, prevStep }) => {
  const [loading, setLoading] = useState(false);

  const { control, handleSubmit, getValues, setError } =
    useFormContext<ForgotPasswordNewPasswordDto>();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const onNext = async () => {
    try {
      setLoading(true);

      await dispatch(
        userActions.forgotPasswordNewPassword({
          newPassword: getValues('newPassword'),
          token: getValues('token'),
        }),
      ).unwrap();

      nextStep();
      AtiraToast.success(t('resetpassword.newPass.success'));
    } catch (e: any) {
      AtiraToast.apiError(e, { autoClose: 6000 });
      console.log(e);
      onSetTypedCRUDErrors(e, setError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Wrapper>
      <Flex flexDirection="column" gap="l" width={'100%'}>
        <ForgotPasswordBackStepButton onClick={prevStep} />

        <Text>{t('resetpassword.newPass.title')}</Text>

        <Text color="fgSecondary">
          {t('resetpassword.newPass.description')}
        </Text>
      </Flex>

      <Flex flexDirection="column" gap="m" marginTop="xl" width={'100%'}>
        <Controller
          name="newPassword"
          control={control}
          rules={{ required: true }}
          render={({ field: { value, onChange }, formState: { errors } }) => (
            <StyledInput
              title={t('resetpassword.newPass.input.placeholder')}
              value={value}
              onChange={onChange}
              type="password"
              id="newpassword"
              valid={isEmpty(errors.newPassword)}
              placeholder={t('resetpassword.newPass.input.placeholder')}
              required
            />
          )}
        />
        <StyledButton onClick={handleSubmit(onNext)} loading={loading}>
          {t('resetpassword.newPass.btn')}
        </StyledButton>
      </Flex>
    </Wrapper>
  );
};
